import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiResponse } from 'model/response-model';
import { AssignBulkTicket, AssignSingleTicket, TicketCategoryModel, TicketDto, TicketDtoPaginationResult, TicketQuery, UpdateBulkTicket, UpdateTicketStatus, AssignedStatusDetails, AssignedStatisticsCount, ChatData } from 'model/request-model';
import { baseQueryWithReauth } from './config/interceptor';


const basePath = 'Ticket';

// Define a service using a base URL and expected endpoints
export const requestApi = createApi({
  reducerPath: 'requestApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Request'],
  endpoints: (builder) => ({
    addCategory: builder.mutation<ApiResponse<TicketCategoryModel>, any>({
      query: (credentials) => ({
        url: `TicketCategory`,
        method: 'POST',
        body: credentials,
      }),
    }),
    addTicket: builder.mutation<ApiResponse<TicketDto>, FormData>({
      query: (credentials) => ({
        url: `${basePath}`,
        method: 'POST',
        body: credentials,
      }),
    }),
    getAllTicket: builder.query<ApiResponse<TicketDtoPaginationResult>, TicketQuery>({
      query: (query) => ({
        url: `${basePath}`,
        params: query
      }),
    }),  
    getTicketById: builder.query<ApiResponse<TicketDto>, string>({
      query: (ticketid) => ({
        url: `${basePath}/${ticketid}`,
      }),
    }),  
    getAllCategories: builder.query<ApiResponse<TicketCategoryModel[]>, void>({
      query: () => ({
        url: `TicketCategory/get-all-categories`,
      }),
    }),    
    updateBulkTicket: builder.mutation<ApiResponse<TicketDto[]>, UpdateBulkTicket>({
      query: (credentials) => ({
        url: `${basePath}`,
        method: 'PATCH',
        body: credentials
      }),
    }),
    updateTicketStatus: builder.mutation<ApiResponse<TicketDto[]>, UpdateTicketStatus>({
      query: (credentials) => ({
        url: `${basePath}/${credentials?.ticketId}/status/${credentials?.status}`,
        method: 'PATCH',
      }),
    }),
    assignBulkTicket: builder.mutation<ApiResponse<TicketDto[]>, AssignBulkTicket>({
      query: (credentials) => ({
        url: `${basePath}/bulk`,
        method: 'PUT',
        body: credentials
      }),
    }),  
    assignSingleTicket: builder.mutation<ApiResponse<TicketDto>, AssignSingleTicket>({
      query: (credentials) => ({
        url: `${basePath}/${credentials?.ticketId}/assign-actions`,
        method: 'PUT',
        body: credentials
      }),
    }),  
    replyTicket: builder.mutation<ApiResponse<TicketDto[]>, [string, FormData]>({
      query: ([ticketId, formData]) => ({
        url: `${basePath}/${ticketId}/reply`,
        method: 'POST',
        body: formData
      }),
    }),
    getAllStatistics: builder.query<ApiResponse<AssignedStatisticsCount>, void>({
      query: () => ({
        url: `${basePath}/statistics`,
      }),
    }),   
    getStatisticsByUser: builder.query<ApiResponse<AssignedStatisticsCount>, string>({
      query: (userId) => ({
        url: `${basePath}/statistics/${userId}`,
      }),
    }),   
    getAssignedTicketCount: builder.query<ApiResponse<AssignedStatisticsCount>, string>({
      query: (userId) => ({
        url: `${basePath}/get-assigned-ticket-count/${userId}`,
      }),
    }), 
    getAssignedTicket: builder.query<ApiResponse<AssignedStatusDetails[]>, [string, string]>({
      query: ([userId, status]) => ({
        url: `${basePath}/get-assigned-ticket/${userId}/${status}`,
      }),
    }),
    deleteTicket: builder.mutation<ApiResponse<TicketDto>, string>({
      query: (ticketId) => ({
        url: `${basePath}/${ticketId}`,
        method: 'DELETE',
      }),
    }),
    getChatHistoryByChatId: builder.query<ApiResponse<ChatData>, string>({
      query: (chatId) => ({
        url: `${basePath}/get-chat-history-by/${chatId}`,
      }),
    }),   
  }),
});


// Export the auto-generated hooks
export const {
  useAddCategoryMutation,
  useAddTicketMutation,
  useGetAllTicketQuery,
  useGetTicketByIdQuery,
  useGetAllCategoriesQuery,
  useUpdateBulkTicketMutation,
  useReplyTicketMutation,
  useAssignSingleTicketMutation,
  useUpdateTicketStatusMutation,
  useGetAllStatisticsQuery,
  useGetStatisticsByUserQuery,
  useDeleteTicketMutation,
  useAssignBulkTicketMutation,
  useGetAssignedTicketCountQuery,
  useGetAssignedTicketQuery,
  useGetChatHistoryByChatIdQuery
} = requestApi;
